import React, { useEffect, useState } from 'react';

import { useFetchModal } from 'candidate_profile_redesign/shared/form/useFetchModal';
import LoadingModal from './loading_modal';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import Form, {
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { PrimaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid';
import { withLocalePrefix } from 'shared/utils/translation';
import {
  AttachmentsField,
  DropdownOption,
  RichTextField,
  SimpleFormSingleDropdown,
  SimpleFormTextField,
  ModalBodyWrapper,
  PaginatedMultiDropdown,
  StackWithHiddenComponents,
} from 'candidate_profile_redesign/shared/form/Fields';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import { useCandidateProfile } from 'candidate_profile_redesign/store';
import { CheckboxField } from 'candidate_profile_redesign/shared/form/Fields';
import DomainWarningCallout from 'candidate_profile_redesign/shared/form/EmailDomainWarningCallout';
import rollbar from 'shared/utils/rollbar_logger';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import OfficeAndTemplateFields from './email/office_and_template_fields';
import SendLaterRow from './email/send_later_row';
import { EmailCandidateProps, TemplateResponse } from '../shared/types';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import CancelButton from '@grnhse/seedling/lib/birch/components/forms/cancel_button';
import { attachmentPayload } from './shared/utils';
import { format } from 'date-fns';
import { useEmailPermissions } from 'candidate_profile_redesign/shared/hooks/useEmailPermissions';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import { validCreatedEmails } from 'candidate_profile_redesign/shared/email_validation';

const t = withLocalePrefix('candidate_profile_redesign.modals.email');

const EmailModalForm = ({
  showOfficeSpecificTemplateDropdown,
  officeOptionsPath,
  fromOptions,
  toOptions,
  customTimeOptions,
  sendEmailWhenOptions,
  canUseAiEmailEditor,
  templateOptions,
  allowSendLater,
  attachments: offerLetters,
  schedulingLinks,
}: EmailCandidateProps) => {
  const {
    paths: { getCcOptionsPath },
    state: {
      currentUser: {
        date_format_birch_datepicker,
        time_zone,
        permissions: { shouldDisplayEmailDomainWarning, canUseSchedulingLinks },
      },
    },
  } = useCandidateProfile();

  const { watch, setValue } = useFormContext();

  const template = watch('template');

  const [templateLoading, setTemplateLoading] = useState(false);

  const handleTemplateChange = async (newTemplate: Item) => {
    if (!newTemplate) {
      return;
    }
    try {
      setTemplateLoading(true);
      const {
        data: { from, subject, html_body, cc, attachment: attachments },
      } = await standardRequestWithErrorFlash.get<TemplateResponse>(
        newTemplate.value
      );
      //fromOptions includes extra data we need (domain, verified status), so lookup value there
      const fromValue = fromOptions.find((option) => option.value === from);
      setValue('from', fromValue);
      setValue(
        'cc',
        cc.map((email: string) => ({ label: email, value: email }))
      );
      setValue('subject', subject);
      setValue('html_body', html_body);
      //if we have offer letters present, don't clear them when template changes
      if (offerLetters?.length > 0) {
        attachments.push(...offerLetters);
      }
      setValue('default_attachments', attachments);
    } finally {
      setTemplateLoading(false);
    }
  };

  useEffect(() => {
    if (!template) {
      return;
    }
    handleTemplateChange(template);
  }, [template]);

  return (
    <StackWithHiddenComponents>
      {shouldDisplayEmailDomainWarning && <DomainWarningCallout compact />}
      <OfficeAndTemplateFields
        templateOptions={templateOptions}
        showOfficeSpecificTemplateDropdown={showOfficeSpecificTemplateDropdown}
        officeOptionsPath={officeOptionsPath}
      />
      <SimpleFormSingleDropdown
        items={fromOptions}
        name={`from`}
        label={t('from')}
        rules={{ required: t('from_required') }}
        clearable={false}
        disabled={templateLoading}
        isPiiBlurred
      />
      <SimpleFormSingleDropdown
        items={toOptions}
        name={`to`}
        label={t('to')}
        rules={{ required: t('to_required') }}
        clearable={false}
        disabled={templateLoading}
        isPiiBlurred
      />
      <Stack spacing={1}>
        <PaginatedMultiDropdown
          getOptionsUrl={getCcOptionsPath}
          name="cc"
          label={t('cc')}
          creatable={true}
          rules={{
            validate: (value: DropdownOption[]) => validCreatedEmails(value),
          }}
          disabled={templateLoading}
          isPiiBlurred
        />
        <CheckboxField
          label={t('include_self')}
          name="include_self"
          value="include_self"
        />
      </Stack>
      <SimpleFormTextField
        id="subject"
        name={`subject`}
        label={t('subject')}
        rules={{ required: t('subject_required') }}
        disabled={templateLoading}
        inputClassName="pii-blur"
      />
      <div className="pii-blur">
        <RichTextField
          compact
          name="html_body"
          isAiEnabled={canUseAiEmailEditor}
          useLegacyEditor
          rules={{ required: t('body_required') }}
          disabled={templateLoading}
          showSchedulingLinksMenu={canUseSchedulingLinks}
          schedulingLinks={schedulingLinks}
        />
      </div>
      <AttachmentsField allowMultiple={true} />
      {allowSendLater && (
        <SendLaterRow
          dateFormatBirchPicker={date_format_birch_datepicker}
          whenEmailOptions={sendEmailWhenOptions}
          customTimeOptions={customTimeOptions}
          timezone={time_zone}
        />
      )}
    </StackWithHiddenComponents>
  );
};

const SubmitButton = () => {
  const {
    watch,
    formState: { isSubmitting },
  } = useFormContext();

  const when = watch('when');
  const { canEmail, helperText } = useEmailPermissions();

  if (!canEmail) {
    return (
      <Tooltip label={helperText} ariaLabel={helperText}>
        <PrimaryButton
          disabled={true}
          data-provides="submit-email-candidate"
          type="submit"
        >
          {t('send_email')}
        </PrimaryButton>
      </Tooltip>
    );
  }

  return (
    <PrimaryButton
      loading={isSubmitting}
      disabled={isSubmitting}
      data-provides="submit-email-candidate"
      type="submit"
    >
      {when && when.value === 'now' ? t('send_email') : t('schedule_email')}
    </PrimaryButton>
  );
};

const ModalFooter = ({ handleClose }: { handleClose: () => void }) => {
  return (
    <DataEntryModal.Footer>
      <Grid item>
        <CancelButton
          data-provides="cancel-email-candidate"
          onClick={handleClose}
        >
          {t('cancel')}
        </CancelButton>
      </Grid>
      <Grid item>
        <SubmitButton />
      </Grid>
    </DataEntryModal.Footer>
  );
};

type SendEmailForm = {
  to: DropdownOption;
  from: DropdownOption;
  cc: Array<DropdownOption>;
  html_body: string;
  subject: string;
  include_self: boolean;
  time: DropdownOption;
  when: DropdownOption;
  sendDate: Date | null;
  default_attachments: any[];
  attachments: any[];
  template?: Item;
};

type EmailCandidateModalProps = {
  modalPath: string;
  submitModalPath?: string;
  isOpen: boolean;
  title: string;
  maskTitle?: boolean;
  handleClose: (successful: boolean) => void;
  onSubmit?: () => Promise<void> | void;
  clearSubject?: boolean;
};

const EmailCandidateModal = ({
  modalPath,
  isOpen,
  handleClose,
  title,
  submitModalPath,
  onSubmit,
  maskTitle = false,
  clearSubject = false,
}: EmailCandidateModalProps) => {
  const {
    actions: { refreshHeader },
    state: {
      currentUser: { date_format_birch_datepicker },
    },
  } = useCandidateProfile();
  const { data: options, loading } = useFetchModal<any>(modalPath, isOpen);

  const onModalSubmit = async ({
    from,
    to,
    cc,
    subject,
    when,
    time,
    sendDate,
    html_body,
    include_self,
    default_attachments,
    attachments,
    template,
  }: SendEmailForm): Promise<void> => {
    const formData = new FormData();
    formData.append('from', from.value);
    formData.append(`to`, to.value);
    const formCc = cc
      .map((option) => (option.userCreated ? option.label : option.value))
      .join(',');
    formData.append('cc', formCc);
    formData.append('subject', subject);
    formData.append('html_body', html_body);
    formData.append('send_at', when.value);
    formData.append('email_time', time?.value ?? '');
    let date: string | null = null;
    if (sendDate) {
      date = format(sendDate, date_format_birch_datepicker);
    }
    formData.append(`email_date`, date ?? '');

    const isScheduled = when.value !== 'now';

    include_self && formData.append(`include_self`, '1');
    if (isScheduled) {
      formData.append(`scheduled`, '1');
    }

    if (template) {
      const match = template.value.match(/email_templates\/(\d+)/);
      const templateId = match ? match[1] : null;
      if (templateId) {
        formData.append('email_template_id', templateId);
      }
    }

    const attachmentFormData = attachmentPayload(
      options.attachments,
      default_attachments,
      attachments
    );
    for (const [key, value] of attachmentFormData.entries()) {
      formData.append(key, value);
    }

    const submitPath = submitModalPath ?? modalPath;

    try {
      const successMessage = isScheduled
        ? t('schedule.email_status')
        : t('email_sent');

      await standardRequestWithErrorFlash.post(submitPath, formData, {
        successMessage,
      });

      if (isScheduled) {
        await refreshHeader();
      }

      await onSubmit?.();
    } catch (e) {
      rollbar.error(e);
    } finally {
      handleClose(true);
    }
  };

  if (loading) {
    return (
      <LoadingModal
        submitButtonText={t('send_email')}
        modalTitle={title}
        maskTitle={maskTitle}
      />
    );
  }

  const fetchDefault = async () => {
    return {
      subject: clearSubject ? '' : options?.subject,
      from: options?.from,
      to: options?.to,
      cc: [],
      when: options?.sendEmailWhenOptions?.[0],
      default_attachments: options?.attachments,
    };
  };

  return (
    <Form<SendEmailForm> onSubmit={onModalSubmit} defaultValues={fetchDefault}>
      <DataEntryModal
        size={'large'}
        isOpen={isOpen}
        onDismiss={() => handleClose(false)}
        onSubmit={() => {
          return;
        }}
        data-provides="email-candidate-modal"
        ariaLabel={'email-candidate-modal'}
      >
        <DataEntryModal.Header onClick={() => handleClose(false)}>
          <span className={maskTitle ? 'pii-blur' : undefined}>{title}</span>
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <ModalBodyWrapper>
            <EmailModalForm {...options} />
          </ModalBodyWrapper>
        </DataEntryModal.Body>
        <ModalFooter handleClose={() => handleClose(false)} />
      </DataEntryModal>
    </Form>
  );
};

export default EmailCandidateModal;
